import { useEffect, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import {
  User,
  Users,
  Flag,
  Swords,
  ListOrdered,
  UsersRound,
  Clock,
} from "lucide-react";
import { Clubhouse_LeagueType, Clubhouse_LeagueScoring } from "../../../enums";
import { leagueTypeLabel, scoringLabel } from "../../helpers";
import { OptionCard } from "../../common/OptionCard";
import { ProgressiveRow, ProgressiveRowGroup } from "../ProgressiveRow";
import { wizardLayout } from "../wizardLayout";
import {
  PointValuesFields,
  SideContestsFields,
  MatchTypeFields,
} from "./scoring/formatScoringRows";
import { Clubhouse_LgMatchPlayScoringMode } from "../../../enums";
import type { AdvancedLeagueFormValues } from "../advancedLeagueSchema";

const FORMAT_OPTIONS: {
  value: Clubhouse_LeagueType;
  title: string;
  description: string;
  icon: ReactNode;
}[] = [
  {
    value: Clubhouse_LeagueType.INDIVIDUAL,
    title: "Individual",
    description: "Players compete on their own.",
    icon: <User className="size-6" />,
  },
  {
    value: Clubhouse_LeagueType.TEAM,
    title: "Team",
    description: "Players compete together as teams.",
    icon: <Users className="size-6" />,
  },
  {
    value: Clubhouse_LeagueType.SCRAMBLE,
    title: "Scramble",
    description: "Teams combine shots into one score.",
    icon: <UsersRound className="size-6" />,
  },
];

const SCORING_OPTIONS: {
  value: Clubhouse_LeagueScoring;
  description: string;
  icon: ReactNode;
}[] = [
  {
    value: Clubhouse_LeagueScoring.STROKE,
    description: "Simple, familiar scoring",
    icon: <Flag className="size-6" />,
  },
  {
    value: Clubhouse_LeagueScoring.MATCH,
    description: "Head-to-head competition",
    icon: <Swords className="size-6" />,
  },
  {
    value: Clubhouse_LeagueScoring.POINTS,
    description: "Flexible points system",
    icon: <ListOrdered className="size-6" />,
  },
];

type RowKey =
  | "leagueDetails"
  | "leagueFormat"
  | "scoringFormat"
  | "matchPlayOptions"
  | "matchType"
  | "pointValues"
  | "sideContests"
  | "teamMatchMode"
  | "teamSetup";

const num = (v: unknown) => (v == null ? 0 : Number(v));

// Rows revealed by an earlier choice glide in, so the form visibly takes shape.
const revealCls =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-300";

export function StructureTab() {
  const {
    watch,
    setValue,
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext<AdvancedLeagueFormValues>();
  const name = watch("name");
  const leagueType = watch("settings.leagueType");
  const scoringType = watch("settings.leagueScoringType");
  const allowAsyncMatch = watch("settings.allowAsynchronousMatchPlay");
  const matchPlay = watch("scoringPolicy.matchPlay");
  const customPoints = watch("scoringPolicy.pointsLeague.customPoints");
  const sideContests = watch("scoringPolicy.sideContests");

  const isTeam = leagueType === Clubhouse_LeagueType.TEAM;
  const isScramble = leagueType === Clubhouse_LeagueType.SCRAMBLE;
  const isTeamBased = isTeam || isScramble;
  const isMatch = scoringType === Clubhouse_LeagueScoring.MATCH;
  const isPoints = scoringType === Clubhouse_LeagueScoring.POINTS;
  const hasSideContests = isMatch || isPoints;
  const opponentWord = isTeamBased ? "teams" : "players";

  // Collapsed-row summaries for the folded-in scoring rows.
  const holeByHole =
    matchPlay?.matchScoringMode ===
    Clubhouse_LgMatchPlayScoringMode.HOLE_BY_HOLE;
  const matchTypeSummary = !matchPlay?.matchScoringMode
    ? "Choose a match type"
    : holeByHole
      ? `Hole-by-Hole · Won ${num(matchPlay?.holeWonPoints)} / Tied ${num(matchPlay?.holeTiedPoints)} / Lost ${num(matchPlay?.holeLostPoints)}`
      : `Head-to-Head · Win ${num(matchPlay?.winPoints)} / Tie ${num(matchPlay?.tiePoints)} / Loss ${num(matchPlay?.lossPoints)}`;
  const pointValuesSummary = `${num(customPoints?.doubleBogey)} / ${num(
    customPoints?.bogey,
  )} / ${num(customPoints?.par)} / ${num(customPoints?.birdie)} / ${num(
    customPoints?.eagle,
  )}`;
  const enabledContests = [
    sideContests?.closestToPin?.enabled && "Closest to Pin",
    sideContests?.longDrive?.enabled && "Long Drive",
    sideContests?.skins?.enabled && "Skins",
  ].filter(Boolean);
  const sideContestsSummary = enabledContests.length
    ? enabledContests.join(", ")
    : "None";

  // Multi-open: each row follows an "auto" default (open while it still needs a
  // choice) until the operator explicitly toggles it, after which the override
  // wins. Settled rows collapse to show their value; this also collapses rows
  // once edit-mode hydration fills them in.
  const [overrides, setOverrides] = useState<Partial<Record<RowKey, boolean>>>(
    {},
  );
  const rowOpen = (key: RowKey, auto: boolean) => overrides[key] ?? auto;
  const setRowOpen = (key: RowKey) => (open: boolean) =>
    setOverrides((o) => ({ ...o, [key]: open }));

  // A duplicate-name rejection lands back on this step. Open the details row so
  // the message sits on the field instead of staying folded inside it.
  const nameError = errors.name?.message;
  useEffect(() => {
    if (!nameError) return;
    setOverrides((o) =>
      o.leagueDetails ? o : { ...o, leagueDetails: true },
    );
  }, [nameError]);

  const set = (field: Parameters<typeof setValue>[0], value: unknown): void =>
    setValue(field, value as never, { shouldDirty: true, shouldValidate: true });

  const chooseFormat = (type: Clubhouse_LeagueType) => {
    set("settings.leagueType", type);
    if (type === Clubhouse_LeagueType.SCRAMBLE) {
      // Scramble is scored as Stroke Play, so match-play options no longer apply.
      set("settings.leagueScoringType", Clubhouse_LeagueScoring.STROKE);
      set("settings.allowAsynchronousMatchPlay", undefined);
    }
    if (type === Clubhouse_LeagueType.INDIVIDUAL) {
      // Individual isn't team-based — drop the team-only values so a prior
      // Team/Scramble selection isn't persisted invisibly.
      set("settings.leagueTeamSize", undefined);
      set("settings.allowAsynchronousTeamPlay", undefined);
    } else {
      // Team/Scramble: two players per team, teammates always play together.
      set("settings.leagueTeamSize", 2);
      set("settings.allowAsynchronousTeamPlay", false);
    }
  };

  const chooseScoring = (scoring: Clubhouse_LeagueScoring) => {
    set("settings.leagueScoringType", scoring);
    // Match Play Options is its own required choice — picking Match here must
    // not silently answer it; the operator clicks that row directly.
    if (scoring !== Clubhouse_LeagueScoring.MATCH) {
      // Drop the match-only option so it isn't persisted for a non-match league.
      set("settings.allowAsynchronousMatchPlay", undefined);
    }
    // Point values ship with defaults, so the row reads as settled and would
    // stay folded — easy to miss. Opening it on an explicit pick (not on edit
    // hydration) puts the values in front of the operator.
    if (scoring === Clubhouse_LeagueScoring.POINTS) {
      setRowOpen("pointValues")(true);
    }
  };

  const detailsSettled = !!name?.trim();

  return (
    <ProgressiveRowGroup>
      <ProgressiveRow
        label="League Details"
        value={detailsSettled ? name : "Add a name"}
        settled={detailsSettled}
        open={!!nameError || rowOpen("leagueDetails", !detailsSettled)}
        onOpenChange={setRowOpen("leagueDetails")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="league-name"
              className="text-sm font-medium text-text-primary"
            >
              League name
            </label>
            <input
              id="league-name"
              type="text"
              aria-invalid={errors.name ? true : undefined}
              placeholder="e.g. Summer Stroke Play League"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              {...register("name", {
                onChange: () => clearErrors("name"),
              })}
            />
            {errors.name?.message && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="league-description"
              className="text-sm font-medium text-text-primary"
            >
              Description
            </label>
            <textarea
              id="league-description"
              rows={2}
              placeholder="A short description players will see when they view this league"
              className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              {...register("description")}
            />
          </div>
        </div>
      </ProgressiveRow>

      <ProgressiveRow
        label="League Format"
        value={leagueType ? leagueTypeLabel[leagueType] : "Choose a format"}
        settled={!!leagueType}
        open={rowOpen("leagueFormat", !leagueType)}
        onOpenChange={setRowOpen("leagueFormat")}
      >
        <div className={wizardLayout.choices}>
          {FORMAT_OPTIONS.map((o) => (
            <OptionCard
              key={o.value}
              className={wizardLayout.choice}
              icon={o.icon}
              title={o.title}
              description={o.description}
              align="left"
              compact
              selected={leagueType === o.value}
              onClick={() => chooseFormat(o.value)}
            />
          ))}
        </div>
        {errors.settings?.leagueType && (
          <p className="mt-2 text-sm text-destructive">
            {errors.settings.leagueType.message}
          </p>
        )}
      </ProgressiveRow>

      {leagueType && (
        <ProgressiveRow
          label="Scoring Format"
          className={revealCls}
          value={
            isScramble
              ? scoringLabel[Clubhouse_LeagueScoring.STROKE]
              : scoringType
                ? scoringLabel[scoringType]
                : "Choose a scoring format"
          }
          locked={isScramble}
          settled={!!scoringType}
          open={rowOpen("scoringFormat", !isScramble && !scoringType)}
          onOpenChange={setRowOpen("scoringFormat")}
        >
          {isScramble ? (
            <p className="text-sm text-text-muted">
              Scramble leagues are scored as Stroke Play.
            </p>
          ) : (
            <div className={wizardLayout.choices}>
              {SCORING_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  className={wizardLayout.choice}
                  icon={o.icon}
                  title={scoringLabel[o.value]}
                  description={o.description}
                  align="left"
                  compact
                  selected={scoringType === o.value}
                  onClick={() => chooseScoring(o.value)}
                />
              ))}
            </div>
          )}
        </ProgressiveRow>
      )}

      {isMatch && (
        <ProgressiveRow
          label="Match Play Options"
          className={revealCls}
          value={
            allowAsyncMatch == null
              ? "Choose how opponents play"
              : allowAsyncMatch
                ? `Opposing ${opponentWord} can play separately`
                : `Opposing ${opponentWord} must play together`
          }
          settled={allowAsyncMatch != null}
          open={rowOpen("matchPlayOptions", allowAsyncMatch == null)}
          onOpenChange={setRowOpen("matchPlayOptions")}
        >
          <div className={wizardLayout.choices}>
            <OptionCard
              className={wizardLayout.choice}
              icon={<UsersRound className="size-6" />}
              title="Must play together"
              description="Opponents share one session"
              align="left"
              compact
              selected={allowAsyncMatch === false}
              onClick={() => set("settings.allowAsynchronousMatchPlay", false)}
            />
            <OptionCard
              className={wizardLayout.choice}
              icon={<Clock className="size-6" />}
              title="Can play at different times"
              description="Opponents finish on their own time"
              align="left"
              compact
              selected={allowAsyncMatch === true}
              onClick={() => set("settings.allowAsynchronousMatchPlay", true)}
            />
          </div>
          {errors.settings?.allowAsynchronousMatchPlay && (
            <p className="mt-2 text-sm text-destructive">
              {errors.settings.allowAsynchronousMatchPlay.message}
            </p>
          )}
        </ProgressiveRow>
      )}

      {isMatch && (
        <ProgressiveRow
          label="Match Type"
          className={revealCls}
          value={matchTypeSummary}
          settled={!!matchPlay?.matchScoringMode}
          open={rowOpen("matchType", !matchPlay?.matchScoringMode)}
          onOpenChange={setRowOpen("matchType")}
        >
          <MatchTypeFields />
        </ProgressiveRow>
      )}

      {isPoints && (
        <ProgressiveRow
          label="Point Values"
          className={revealCls}
          value={pointValuesSummary}
          settled
          open={rowOpen("pointValues", false)}
          onOpenChange={setRowOpen("pointValues")}
        >
          <PointValuesFields />
        </ProgressiveRow>
      )}

      {hasSideContests && (
        <ProgressiveRow
          label="Side Contests"
          className={revealCls}
          value={sideContestsSummary}
          settled
          open={rowOpen("sideContests", false)}
          onOpenChange={setRowOpen("sideContests")}
        >
          <SideContestsFields />
        </ProgressiveRow>
      )}

      {isTeam && isMatch && (
        <ProgressiveRow
          label="Team Match Mode"
          className={revealCls}
          value="Standard Team Match"
          locked
          open={rowOpen("teamMatchMode", false)}
          onOpenChange={setRowOpen("teamMatchMode")}
        >
          <p className="text-sm text-text-muted">
            Team match play uses the Standard Team Match format.
          </p>
        </ProgressiveRow>
      )}

      {isTeamBased && (
        <ProgressiveRow
          label="Team Setup"
          className={revealCls}
          value="2 players per team"
          locked
          open={rowOpen("teamSetup", false)}
          onOpenChange={setRowOpen("teamSetup")}
        >
          <p className="text-sm text-text-muted">
            Two players per team. Teammates always play together.
          </p>
        </ProgressiveRow>
      )}
    </ProgressiveRowGroup>
  );
}
